import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations, LANGS } from "@/data/landing";

const I18nContext = createContext({
  lang: "en",
  t: (k) => k,
  setLang: () => {},
});

const STORAGE_KEY = "hb-lang";

function detectInitial() {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && LANGS[stored]) return stored;
  } catch (_) {}
  const nav = (typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage)) || "en";
  return nav.toLowerCase().startsWith("tr") ? "tr" : "en";
}

function getDeep(obj, path) {
  return path.split(".").reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState("en");

  useEffect(() => {
    const initial = detectInitial();
    setLangState(initial);
    try {
      document.documentElement.lang = initial;
    } catch (_) {}
  }, []);

  const setLang = useCallback((next) => {
    if (!LANGS[next]) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
      document.documentElement.lang = next;
    } catch (_) {}
  }, []);

  const t = useCallback(
    (path, fallback = "") => {
      const v = getDeep(translations[lang], path);
      if (v === undefined || v === null) {
        const en = getDeep(translations.en, path);
        return en !== undefined ? en : fallback || path;
      }
      return v;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
